import React, { useState, useEffect, useRef, useContext } from 'react';
import { Container, Row, Col, Card, Table, Badge, Button, Form, Pagination, Placeholder } from 'react-bootstrap';
import { Link } from 'react-router-dom';
import { 
  FaPlus, 
  FaSearch, 
  FaFilter, 
  FaTimes,
  FaEye,
  FaBuilding,
  FaUser,
  FaCalendarAlt,
  FaSortAmountDown,
  FaTicketAlt
} from 'react-icons/fa';
import LoadingSpinner from '../components/LoadingSpinner';
import { AuthContext } from '../context/AuthContext';
import ticketService from '../services/ticketService';
import branchService from '../services/branchService';
import userService from '../services/userService';
import { formatDate, formatNumber } from '../utils/format';
import { ISSUE_TYPE_OPTIONS, translateIssueTypes } from '../utils/issueTypes';
import { getRoleLabel, isViewer, canWriteTickets } from '../utils/rbac';

const TicketList = () => {
  const { user } = useContext(AuthContext);
  const [tickets, setTickets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [branches, setBranches] = useState([]);
  const [assignableUsers, setAssignableUsers] = useState([]);
  const userRole = user?.role || '';
  const canCreateTickets = canWriteTickets(userRole) && !isViewer(userRole);
  const [filters, setFilters] = useState({
    status: '',
    issue_type: '',
    branch_id: '',
    assigned_to: '',
    search: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    total: 0,
    totalPages: 0
  });
  const [showAdvanced, setShowAdvanced] = useState(false);
  
  const debounceTimerRef = useRef(null);
  const requestSequenceRef = useRef(0);
  const skipInitialFilterFetchRef = useRef(true);
  const skipInitialSearchFetchRef = useRef(true);
  const skipNextSearchFetchRef = useRef(false);
  // Track the "canonical" page for fetch so setPagination + fetch don't race
  const fetchParamsRef = useRef({ filters, page: 1, limit: 10 });

  const fetchBranches = async () => {
    try {
      const data = await branchService.getAll();
      setBranches(data.branches || data);
    } catch (err) {
      console.error(err);
    }
  };

  const fetchAssignableUsers = async () => {
    try {
      const data = await userService.getTicketAssignableUsers();
      setAssignableUsers(data.users || data);
    } catch (err) {
      console.error(err);
    }
  };

  // Central fetch function — always reads from fetchParamsRef to avoid stale closures
  const fetchTickets = async (overrides = {}) => {
    const { filters: f, page, limit } = { ...fetchParamsRef.current, ...overrides };
    const requestId = ++requestSequenceRef.current;
    setLoading(true);

    try {
      const filtersCopy = {};
      Object.keys(f).forEach(key => {
        if (f[key] && key !== 'page' && key !== 'limit') {
          filtersCopy[key] = f[key];
        }
      });

      const data = await ticketService.getAll(filtersCopy, { page, limit });

      if (requestId !== requestSequenceRef.current) {
        return;
      }

      setTickets(data.tickets || data.data || []);
      if (data.total !== undefined) {
        setPagination(prev => ({
          ...prev,
          total: data.total,
          totalPages: Math.ceil(data.total / limit)
        }));
      } else if (data.totalPages !== undefined) {
        setPagination(prev => ({
          ...prev,
          total: data.total || prev.total,
          totalPages: data.totalPages
        }));
      }
    } catch (err) {
      console.error('Failed to fetch tickets', err);
    } finally {
      if (requestId === requestSequenceRef.current) {
        setLoading(false);
      }
    }
  };

  // Initial load
  useEffect(() => {
    skipInitialFilterFetchRef.current = true;
    skipInitialSearchFetchRef.current = true;
    fetchBranches();
    fetchAssignableUsers();
    fetchParamsRef.current = { filters, page: 1, limit: 10 };
    fetchTickets();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Filter changes (excluding search which is debounced) — reset page to 1
  useEffect(() => {
    if (skipInitialFilterFetchRef.current) {
      skipInitialFilterFetchRef.current = false;
      return;
    }

    const nextPage = 1;
    fetchParamsRef.current = { ...fetchParamsRef.current, filters, page: nextPage };
    setPagination(prev => ({ ...prev, page: nextPage }));
    fetchTickets({ filters, page: nextPage });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.status, filters.issue_type, filters.branch_id, filters.assigned_to]);

  // Search field — debounced
  useEffect(() => {
    if (skipInitialSearchFetchRef.current) {
      skipInitialSearchFetchRef.current = false;
      return undefined;
    }

    if (skipNextSearchFetchRef.current) {
      skipNextSearchFetchRef.current = false;
      return undefined;
    }

    if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    debounceTimerRef.current = setTimeout(() => {
      const nextPage = 1;
      fetchParamsRef.current = { ...fetchParamsRef.current, filters, page: nextPage };
      setPagination(prev => ({ ...prev, page: nextPage }));
      fetchTickets({ filters, page: nextPage });
    }, 300);
    return () => {
      if (debounceTimerRef.current) clearTimeout(debounceTimerRef.current);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filters.search]);

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= pagination.totalPages) {
      fetchParamsRef.current = { ...fetchParamsRef.current, page: newPage };
      setPagination(prev => ({ ...prev, page: newPage }));
      fetchTickets({ page: newPage });
    }
  };

  const clearFilters = () => {
    const clearedFilters = {
      status: '',
      issue_type: '',
      branch_id: '',
      assigned_to: '',
      search: ''
    };
    const willTriggerImmediateFetch = Boolean(
      filters.status || filters.issue_type || filters.branch_id || filters.assigned_to
    );
    skipNextSearchFetchRef.current = willTriggerImmediateFetch;
    setFilters(clearedFilters);
    fetchParamsRef.current = { ...fetchParamsRef.current, filters: clearedFilters, page: 1 };
    setPagination(prev => ({ ...prev, page: 1 }));
  };

  const hasActiveFilters = filters.search || filters.status || filters.issue_type || filters.branch_id || filters.assigned_to;

  if (loading) {
    return (
      <Container fluid className="page-container">
        <div className="dashboard-header">
          <div className="dashboard-header-content">
            <div className="dashboard-header-icon">
              <FaTicketAlt />
            </div>
            <div>
              <h1 className="dashboard-header-title">تذاكر الدعم الفني</h1>
              <p className="dashboard-header-subtitle">إدارة وتتبع جميع التذاكر</p>
            </div>
          </div>
        </div>

        <Card className="dashboard-card mb-4">
          <Card.Body>
            <Row className="g-3">
              <Col md={6}>
                <div className="skeleton" style={{ height: '38px' }}></div>
              </Col>
              <Col md={6}>
                <div className="skeleton" style={{ height: '38px' }}></div>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="dashboard-card">
          <Card.Body className="p-0">
            <div className="table-responsive-wrapper responsive-table-wrapper">
              <table className="modern-table responsive-data-table">
                <thead>
                  <tr>
                    <th>رقم التذكرة</th>
                    <th>الفرع</th>
                    <th>نوع المشكلة</th>
                    <th>الأولوية</th>
                    <th>المكلف</th>
                    <th>الحالة</th>
                    <th>مهلة SLA</th>
                    <th>التاريخ</th>
                    <th className="text-center">إجراء</th>
                  </tr>
                </thead>
                <tbody>
                  {[...Array(8)].map((_, idx) => (
                    <tr key={idx}>
                      {[...Array(9)].map((__, cIdx) => (
                        <td key={cIdx}>
                          <div className="skeleton skeleton-text" style={{ width: cIdx === 3 ? '60%' : '40%' }}></div>
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card.Body>
        </Card>
      </Container>
    );
  }

  return (
    <Container fluid className="page-container">
      {/* Page Header */}
      <div className="dashboard-header">
        <div className="dashboard-header-content">
          <div className="dashboard-header-icon">
            <FaTicketAlt />
          </div>
          <div>
            <h1 className="dashboard-header-title">تذاكر الدعم الفني</h1>
            <p className="dashboard-header-subtitle">إدارة وتتبع جميع التذاكر ({pagination.total} تذكرة)</p>
          </div>
        </div>
        <div className="page-actions">
          {!isViewer(userRole) && (
            <Link to="/tickets/new">
              <Button className="btn-action-create" title="إنشاء تذكرة جديدة">
                <FaPlus className="btn-icon" /> إنشاء تذكرة جديدة
              </Button>
            </Link>
          )}
        </div>
      </div>

      {/* Filter Card */}
      <Card className="dashboard-card mb-4">
        <Card.Body>
          <Row className="g-3 align-items-end">
            <Col md={6} lg={4}>
              <Form.Group className="mb-0">
                <Form.Label className="filter-label">البحث</Form.Label>
                <div className="search-wrapper">
                  <span className="search-icon"><FaSearch /></span>
                  <Form.Control
                    type="text"
                    placeholder="بحث برقم التذكرة، الوصف، أو اسم الفني..."
                    value={filters.search}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="search-input"
                  />
                </div>
              </Form.Group>
            </Col>
            
            <Col md={6} lg={2}>
              <Form.Group className="mb-0">
                <Form.Label className="filter-label">الحالة</Form.Label>
                <Form.Select
                  value={filters.status}
                  onChange={(e) => setFilters({ ...filters, status: e.target.value })}
                  className="form-select-custom"
                >
                  <option value="">الكل</option>
                  <option value="new">جديدة</option>
                  <option value="in_progress">قيد العمل</option>
                  <option value="on_hold">معلقة</option>
                  <option value="resolved">محلولة</option>
                </Form.Select>
              </Form.Group>
            </Col>
            
            <Col md={6} lg={2}>
              <Form.Group className="mb-0">
                <Form.Label className="filter-label">النوع</Form.Label>
                <Form.Select
                  value={filters.issue_type}
                  onChange={(e) => setFilters({ ...filters, issue_type: e.target.value })}
                  className="form-select-custom"
                >
                  <option value="">الكل</option>
                  {ISSUE_TYPE_OPTIONS.map((option) => (
                    <option key={option.value} value={option.value}>{option.label}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            
            <Col md={6} lg={2}>
              <Form.Group className="mb-0">
                <Form.Label className="filter-label">الفرع</Form.Label>
                <Form.Select
                  value={filters.branch_id}
                  onChange={(e) => setFilters({ ...filters, branch_id: e.target.value })}
                  className="form-select-custom"
                >
                  <option value="">الكل</option>
                  {branches.map(branch => (
                    <option key={branch.id} value={branch.id}>{branch.name}</option>
                  ))}
                </Form.Select>
              </Form.Group>
            </Col>
            
            <Col md={6} lg={2}>
              <div className="ticket-list-filter-actions d-flex gap-2">
                {hasActiveFilters && (
                  <Button
                    variant="outline-secondary"
                    onClick={clearFilters}
                    className="btn-action-cancel"
                    title="مسح جميع الفلاتر"
                  >
                    <FaTimes className="btn-icon" /> مسح الفلاتر
                  </Button>
                )}
                <Button 
                  variant={showAdvanced ? 'primary' : 'outline-secondary'}
                  onClick={() => setShowAdvanced(!showAdvanced)}
                  className="btn-action-filter"
                  title="تفعيل أو إخفاء الفلاتر المتقدمة"
                >
                  <FaFilter className="btn-icon" /> فلاتر متقدمة
                </Button>
              </div>
            </Col>
          </Row>

          {/* Advanced Filters */}
          {showAdvanced && (
            <Row className="g-3 mt-2 pt-3 border-top">
              <Col md={6} lg={3}>
                <Form.Group className="mb-0">
                  <Form.Label className="filter-label">الفني</Form.Label>
                  <Form.Select
                    value={filters.assigned_to}
                    onChange={(e) => setFilters({ ...filters, assigned_to: e.target.value })}
                    className="form-select-custom"
                  >
                    <option value="">الكل</option>
                    {assignableUsers.map((user) => (
                      <option key={user.id} value={user.id}>
                        {`${user.full_name}${user.username ? ` • @${user.username}` : ''} • ${getRoleLabel(user.role)}`}
                      </option>
                    ))}
                  </Form.Select>
                  <Form.Text className="text-muted small">
                    الفنيون والمشرفون ومديرو النظام
                  </Form.Text>
                </Form.Group>
              </Col>
            </Row>
          )}
        </Card.Body>
      </Card>

      {/* Tickets Table */}
      <Card className="dashboard-card">
        <Card.Body className="p-0">
          <div className="table-responsive-wrapper responsive-table-wrapper">
            <table className="modern-table responsive-data-table">
              <thead>
                <tr>
                  <th>رقم التذكرة</th>
                  <th>الفرع</th>
                  <th>نوع المشكلة</th>
                  <th>الأولوية</th>
                  <th>المكلف</th>
                  <th>الحالة</th>
                  <th>مهلة SLA</th>
                  <th>التاريخ</th>
                  <th className="text-center">إجراء</th>
                </tr>
              </thead>
              <tbody>
                {tickets.length === 0 ? (
                  <tr>
                    <td colSpan="9">
                      <div className="table-empty-state">
                        <div className="table-empty-state-icon">
                          <FaTicketAlt size={48} />
                        </div>
                        <h5 className="empty-state-title">لا توجد تذاكر</h5>
                        <p className="empty-state-description">
                          {hasActiveFilters
                            ? 'لم يتم العثور على تذاكر مطابقة للفلاتر المحددة'
                            : 'لم يتم إنشاء أي تذاكر بعد'}
                        </p>
                        {!hasActiveFilters && (
                          <Link to="/tickets/new">
                            <Button className="btn-create">إنشاء أول تذكرة</Button>
                          </Link>
                        )}
                      </div>
                    </td>
                  </tr>
                ) : (
                  tickets.map(ticket => (
                    <tr key={ticket.id}>
                      <td data-label="رقم التذكرة">
                        <Link to={`/tickets/${ticket.id}`} className="cell-link">
                          #{ticket.ticket_number}
                        </Link>
                      </td>
                      <td data-label="الفرع">
                        <div className="cell-with-icon">
                          <FaBuilding className="cell-icon" />
                          <span>{ticket.branch_name}</span>
                        </div>
                      </td>
                      <td data-label="نوع المشكلة">
                        <span className="cell-badge">{translateIssueTypes(ticket.issue_types)}</span>
                      </td>
                      <td data-label="الأولوية">
                        <PriorityBadge priority={ticket.priority} />
                      </td>
                      <td data-label="المكلف">
                        <div className="cell-with-icon">
                          {ticket.assigned_to_name ? (
                            <>
                              <div className="avatar avatar-sm">
                                {ticket.assigned_to_name.charAt(0)}
                              </div>
                              <span>{ticket.assigned_to_name}</span>
                            </>
                          ) : (
                            <span className="text-muted">غير محدد</span>
                          )}
                        </div>
                      </td>
                      <td data-label="الحالة">
                        <StatusBadge status={ticket.status} />
                      </td>
                      <td data-label="مهلة SLA">
                        <SlaStatusBadge slaStatus={ticket.sla_status} dueAt={ticket.due_at} />
                      </td>
                      <td data-label="التاريخ">
                        <div className="cell-with-icon">
                          <FaCalendarAlt className="cell-icon" />
                          <span className="text-muted">
                            {formatDate(ticket.received_date)}
                          </span>
                        </div>
                      </td>
                      <td data-label="إجراء" className="table-cell-actions">
                        <div className="table-actions">
                          <Link to={`/tickets/${ticket.id}`}>
                            <Button className="btn-action-view" title={`عرض تفاصيل التذكرة #${ticket.ticket_number}`}>
                              <FaEye size={14} />
                              <span>عرض</span>
                            </Button>
                          </Link>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className="pagination-row d-flex justify-content-between align-items-center p-4 border-top">
              <div className="pagination-info text-muted" style={{ fontSize: '0.875rem' }}>
                عرض {formatNumber((pagination.page - 1) * pagination.limit + 1)} إلى {formatNumber(Math.min(pagination.page * pagination.limit, pagination.total))} من {formatNumber(pagination.total)} تذكرة
              </div>
              <Pagination className="mb-0">
                <Pagination.Prev 
                  onClick={() => handlePageChange(pagination.page - 1)}
                  disabled={pagination.page === 1}
                />
                {[...Array(pagination.totalPages)].map((_, idx) => {
                  const pageNum = idx + 1;
                  if (
                    pageNum === 1 ||
                    pageNum === pagination.totalPages ||
                    (pageNum >= pagination.page - 1 && pageNum <= pagination.page + 1)
                  ) {
                    return (
                      <Pagination.Item
                        key={pageNum}
                        active={pageNum === pagination.page}
                        onClick={() => handlePageChange(pageNum)}
                      >
                        {pageNum}
                      </Pagination.Item>
                    );
                  } else if (
                    pageNum === pagination.page - 2 ||
                    pageNum === pagination.page + 2
                  ) {
                    return <Pagination.Ellipsis key={pageNum} />;
                  }
                  return null;
                })}
                <Pagination.Next 
                  onClick={() => handlePageChange(pagination.page + 1)}
                  disabled={pagination.page === pagination.totalPages}
                />
              </Pagination>
            </div>
          )}
        </Card.Body>
      </Card>
    </Container>
  );
};

// Status Badge Component
const StatusBadge = ({ status }) => {
  const statusMap = {
    new: { label: 'جديدة', class: 'status-new' },
    in_progress: { label: 'قيد العمل', class: 'status-in-progress' },
    on_hold: { label: 'معلقة', class: 'status-on-hold' },
    resolved: { label: 'محلولة', class: 'status-resolved' }
  };
  
  const config = statusMap[status] || { label: status, class: 'status-on-hold' };
  
  return <span className={`status-badge ${config.class}`}>{config.label}</span>;
};

// Priority Badge Component
const PriorityBadge = ({ priority }) => {
  const priorityMap = {
    low: { label: 'منخفضة', class: 'priority-low' },
    medium: { label: 'متوسطة', class: 'priority-medium' },
    high: { label: 'عالية', class: 'priority-high' },
    urgent: { label: 'عاجلة', class: 'priority-urgent' }
  };
  
  const config = priorityMap[priority] || { label: priority, class: 'priority-medium' };
  
  return <span className={`priority-badge ${config.class}`}>{config.label}</span>;
};

// SLA Status Badge Component
const SlaStatusBadge = ({ slaStatus, dueAt }) => {
  // Don't show badge for n/a status
  if (!slaStatus || slaStatus === 'n/a') {
    return <span className="sla-badge sla-na">-</span>;
  }
  
  const slaMap = {
    on_track: { label: 'مقبول', class: 'sla-on-track' },
    at_risk: { label: 'مخاطر', class: 'sla-at-risk' },
    breached: { label: 'متأخر', class: 'sla-breached' },
    met: { label: 'محقق', class: 'sla-met' }
  };
  
  const config = slaMap[slaStatus] || { label: slaStatus, class: 'sla-na' };
  
  return <span className={`sla-badge ${config.class}`}>{config.label}</span>;
};

export default TicketList;
